/**
 * Keywords Service - Fetches dynamic keywords from backend API
 * 
 * This service provides access to feature keywords stored in keywords_cz index.
 * Keywords are cached in memory for the session to avoid repeated API calls.
 */

export interface KeywordItem {
  id: string
  label: string
  label_cs?: string
  label_en?: string
  feature_key: string
  description?: string
  weight: number
  icon?: string  // Icon name (e.g., "Fence", "Car") - maps to Lucide icon
}

export interface KeywordCategory {
  name: string
  display_name: string
  keywords: KeywordItem[]
}

export interface KeywordsResponse {
  categories: Record<string, KeywordCategory>
  version: string
  total_keywords: number
}

const cachedKeywordsByLang: Partial<Record<'cs' | 'en', KeywordsResponse>> = {}
const fetchPromiseByLang: Partial<Record<'cs' | 'en', Promise<KeywordsResponse>>> = {}

/**
 * Get keywords from backend API (cached)
 */
export async function getKeywords(lang: 'cs' | 'en' = 'cs'): Promise<KeywordsResponse> {
  const language = lang === 'en' ? 'en' : 'cs'
  // Return cached if available
  if (cachedKeywordsByLang[language]) {
    return cachedKeywordsByLang[language] as KeywordsResponse
  }
  
  // Return existing fetch promise if already fetching
  if (fetchPromiseByLang[language]) {
    return fetchPromiseByLang[language] as Promise<KeywordsResponse>
  }
  
  // Fetch from backend
  const requestUrl = `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080'}/api/v1/keywords?lang=${language}`
  fetchPromiseByLang[language] = fetch(requestUrl, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch keywords: ${response.statusText}`)
      }
      return response.json()
    })
    .then((data: KeywordsResponse) => {
      cachedKeywordsByLang[language] = data
      fetchPromiseByLang[language] = undefined
      return data
    })
    .catch(error => {
      console.error('Error fetching keywords:', error)
      fetchPromiseByLang[language] = undefined
      // Return empty response as fallback
      return {
        categories: {},
        version: new Date().toISOString(),
        total_keywords: 0,
      }
    })
  
  return fetchPromiseByLang[language] as Promise<KeywordsResponse>
}

/**
 * Clear the keywords cache (useful for testing or forcing refresh)
 */
export function clearKeywordsCache() {
  cachedKeywordsByLang.cs = undefined
  cachedKeywordsByLang.en = undefined
  fetchPromiseByLang.cs = undefined
  fetchPromiseByLang.en = undefined
}

/**
 * Default category order for UI display
 * Can be overridden by weight in database later
 */
export function getCategoryOrder(): string[] {
  return [
    'rooms',
    'price',
    'area',
    'amenities',
    'bathroom',
    'building',
    'connectivity',
    'electronics',
    'furniture',
    'furnishing',
    'heating_climate',
    'kitchen',
    'lifestyle',
    'outdoor',
    'parking_storage',

  ]
}

/**
 * Get icon name for category (for UI display)
 */
export function getCategoryIcon(category: string): string {
  const iconMap: Record<string, string> = {
    rooms: 'Building2',
    price: 'FileText',
    area: 'Building2',
    features: 'Sparkles',
    lifestyle: 'PawPrint',
    heating: 'Flame',
    bathroom: 'Bath',
    kitchen: 'UtensilsCrossed',
    furniture: 'Sofa',
    electronics: 'Tv',
    internet: 'Wifi',
    outdoor: 'Trees',
    building: 'Building2',
    parking: 'Car',
    amenities: 'Sparkles',
    contract: 'FileText',
  }
  
  return iconMap[category] || 'Sparkles'
}

